/*!
 * Copyright (c) 2026 Elevation Technologies Pte Ltd.
 * Developer: filimoni@elevationtech.com.fj
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import '../css/main.scss';
import BusinessApp from './components/BusinessApp';
import FsfPageApp from './fsf/FsfPageApp';

const rootElement = document.getElementById('business-react-root');
const fsfRootElement = document.getElementById('fsf-page-root');
const fsfHeader = document.querySelector('.site-header--fsf');
const scrollTopButton = document.querySelector('.scroll-to-top');

const updateScrollTopButton = () => {
  if (!scrollTopButton) {
    return;
  }

  const shouldShow = window.scrollY > 520 && !document.body.classList.contains('is-fsf-menu-open');
  scrollTopButton.classList.toggle('is-visible', shouldShow);
};

if (fsfHeader) {
  const updateHeaderState = () => {
    fsfHeader.classList.toggle('is-scrolled', window.scrollY > 8);
  };
  const mobileToggle = fsfHeader.querySelector('.site-mobile-toggle');
  const mobilePanel = fsfHeader.querySelector('.site-mobile-panel');
  const mobileBreakpoint = window.matchMedia('(max-width: 1040px)');

  const setMobileMenuOpen = (isOpen) => {
    if (!mobileToggle || !mobilePanel) {
      return;
    }

    fsfHeader.classList.toggle('is-mobile-menu-open', isOpen);
    document.body.classList.toggle('is-fsf-menu-open', isOpen);
    updateScrollTopButton();
    mobileToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    const toggleText = mobileToggle.querySelector('.screen-reader-text');

    if (toggleText) {
      toggleText.textContent = isOpen ? 'Close menu' : 'Open menu';
    }

    if (!isOpen) {
      fsfHeader.querySelectorAll('.site-nav-menu-button[aria-controls]').forEach((button) => {
        const menuItem = button.closest('.menu-item-has-children');

        button.setAttribute('aria-expanded', 'false');
        menuItem?.classList.remove('is-open');
      });
    }
  };

  updateHeaderState();
  window.addEventListener('scroll', updateHeaderState, { passive: true });

  if (mobileToggle && mobilePanel) {
    mobileToggle.addEventListener('click', (event) => {
      event.stopPropagation();
      const isOpen = mobileToggle.getAttribute('aria-expanded') === 'true';
      setMobileMenuOpen(!isOpen);
    });

    mobilePanel.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => setMobileMenuOpen(false));
    });

    document.addEventListener('click', (event) => {
      if (!fsfHeader.classList.contains('is-mobile-menu-open')) {
        return;
      }

      if (mobilePanel.contains(event.target) || mobileToggle.contains(event.target)) {
        return;
      }

      setMobileMenuOpen(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    });

    mobileBreakpoint.addEventListener('change', (event) => {
      if (!event.matches) {
        setMobileMenuOpen(false);
      }
    });
  }

  const menuButtons = fsfHeader.querySelectorAll('.site-nav-menu-button[aria-controls]');

  menuButtons.forEach((button) => {
    const menu = document.getElementById(button.getAttribute('aria-controls'));
    const menuItem = button.closest('.menu-item-has-children');

    if (!menu || !menuItem) {
      return;
    }

    const setMenuOpen = (isOpen) => {
      button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      menuItem.classList.toggle('is-open', isOpen);
    };
    let closeTimer;
    let pointerInside = false;

    const openMenu = () => {
      window.clearTimeout(closeTimer);
      pointerInside = true;
      setMenuOpen(true);
    };

    const closeMenu = () => {
      window.clearTimeout(closeTimer);
      pointerInside = false;
      setMenuOpen(false);
    };

    const scheduleMenuClose = () => {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        pointerInside = false;
        setMenuOpen(false);
      }, 220);
    };

    button.addEventListener('click', () => {
      const isOpen = button.getAttribute('aria-expanded') === 'true';

      if (pointerInside && isOpen && !mobileBreakpoint.matches) {
        return;
      }

      setMenuOpen(!isOpen);
    });

    menuItem.addEventListener('mouseenter', () => {
      if (mobileBreakpoint.matches) {
        return;
      }

      openMenu();
    });

    menuItem.addEventListener('mouseleave', () => {
      if (mobileBreakpoint.matches) {
        return;
      }

      scheduleMenuClose();
    });

    menuItem.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeMenu();
        button.focus();
      }
    });

    menuItem.addEventListener('focusout', (event) => {
      if (!menuItem.contains(event.relatedTarget)) {
        closeMenu();
      }
    });

    document.addEventListener('click', (event) => {
      if (!menuItem.contains(event.target)) {
        closeMenu();
      }
    });
  });
}

if (scrollTopButton) {
  updateScrollTopButton();

  scrollTopButton.addEventListener('click', () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  });

  window.addEventListener('scroll', updateScrollTopButton, { passive: true });
  window.addEventListener('resize', updateScrollTopButton);
}

if (fsfRootElement) {
  createRoot(fsfRootElement).render(<FsfPageApp pageSlug={fsfRootElement.dataset.page} />);
} else if (rootElement) {
  createRoot(rootElement).render(<BusinessApp settings={window.BUSINESS_THEME || {}} />);
}
